import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import api from '../lib/api';

/* Owner ask Feb 2026: manager / director can reset their own
   `kiosk_pin` (used at the till for discount approvals, locked-item
   override etc.) from their dashboard. Old PIN + new PIN + confirm,
   4–8 digits only. */
export default function ChangePinDialog({ open, onOpenChange }) {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setCurrentPin(''); setNewPin(''); setConfirmPin(''); };
  const onlyDigits = (v, max = 8) => v.replace(/[^0-9]/g, '').slice(0, max);

  const submit = async () => {
    if (!newPin || newPin.length < 4) { toast.error('New PIN must be 4–8 digits'); return; }
    if (newPin !== confirmPin) { toast.error('New PINs do not match'); return; }
    if (currentPin === newPin) { toast.error('New PIN must differ from current PIN'); return; }
    setSaving(true);
    try {
      await api.put('/auth/change-my-kiosk-pin', { current_pin: currentPin, new_pin: newPin });
      toast.success('Manager PIN updated');
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update PIN');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-serif text-white flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-gold" /> Reset Manager PIN
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <p className="text-[11px] text-zinc-500 font-sans leading-snug">
            Your PIN unlocks till overrides &amp; discount approvals at the floor. Default is <span className="text-zinc-300">0000</span> — change it now to keep the floor honest.
          </p>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Current PIN</Label>
            <Input
              type="password"
              inputMode="numeric"
              value={currentPin}
              onChange={(e) => setCurrentPin(onlyDigits(e.target.value))}
              placeholder="••••"
              className="bg-transparent border border-white/10 text-white font-mono tracking-[0.4em]"
              data-testid="change-pin-current"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">New PIN</Label>
            <Input
              type="password"
              inputMode="numeric"
              value={newPin}
              onChange={(e) => setNewPin(onlyDigits(e.target.value))}
              placeholder="••••"
              className="bg-transparent border border-white/10 text-white font-mono tracking-[0.4em]"
              data-testid="change-pin-new"
            />
            <p className="text-[10px] text-zinc-600 font-sans">4–8 digits</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Confirm New PIN</Label>
            <Input
              type="password"
              inputMode="numeric"
              value={confirmPin}
              onChange={(e) => setConfirmPin(onlyDigits(e.target.value))}
              placeholder="••••"
              className="bg-transparent border border-white/10 text-white font-mono tracking-[0.4em]"
              data-testid="change-pin-confirm"
            />
          </div>
          <Button
            onClick={submit}
            disabled={saving}
            className="w-full bg-gold text-black font-bold rounded-sm font-sans h-10"
            data-testid="change-pin-submit"
          >
            {saving ? 'Saving...' : 'Update PIN'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
